<template>
  <input
    :value="valueDisplayed"
    :class="{ 'input-error': hasError }"
    @input="handleInput"
    @blur="handleBlur"
    :min="valueMin"
    :disabled="isDisabled"
    :placeholder
    inputmode="decimal"
    autocomplete="off"
    spellcheck="false"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    isAllowedNegative?: boolean;
    isAllowedDecimal?: boolean;
    isDisabled?: boolean;
    hasError?: boolean;
    valueMin?: number;
    maxDecimals?: number;
    placeholder?: string;
  }>(),
  {
    isAllowedNegative: false,
    isAllowedDecimal: true,
    maxDecimals: undefined,
    hasError: false,
    isDisabled: false,
  }
);

const modelValue = defineModel<number | null>();
const rawNumberString = ref(modelValue.value?.toString() ?? '');

const valueDisplayed = computed(() => formatNumberStringWithCommas(rawNumberString.value));

const handleInput = (event: Event) => {
  const inputEl = event.target as HTMLInputElement;

  const nextRawString = sanitizeNumberInputString(inputEl.value);

  rawNumberString.value = nextRawString;
  modelValue.value = parseNumberOrNull(nextRawString);
};

const handleBlur = () => {
  const parsedNumber = parseNumberOrNull(rawNumberString.value);
  rawNumberString.value = parsedNumber === null ? '' : removeTrailingDecimalZeros(String(parsedNumber));
};

const parseNumberOrNull = (stringInput: string): number | null => {
  if (isEmptyOrPartialNumberString(stringInput)) return null;

  const parsed = Number(stringInput);
  return Number.isFinite(parsed) ? parsed : null;
};

const isEmptyOrPartialNumberString = (stringInput: string): boolean => {
  if (!stringInput) return true;
  if (stringInput === '-') return true;
  if (props.isAllowedDecimal && (stringInput === '.' || stringInput === '-.')) return true;

  if (!props.isAllowedDecimal && (stringInput === '.' || stringInput === '-.')) return true;

  return false;
};

const sanitizeNumberInputString = (stringInput: string): string => {
  // 1) remove commas/spaces
  let sanitized = removeSeparators(stringInput);

  // 2) keep only digits + "-" + "." (then optionally remove "." later)
  sanitized = keepOnlyNumberChars(sanitized);

  // 3) enforce negative rules
  sanitized = applyNegativeRule(sanitized, props.isAllowedNegative);

  // 4) enforce decimal rules (remove dot OR keep one dot + limit decimals)
  sanitized = applyDecimalRule(sanitized, props.isAllowedDecimal, props.maxDecimals);

  // 5) allow partial typing states
  if (isAllowedPartialState(sanitized, props.isAllowedDecimal)) return sanitized;

  // 6) normalize leading zeros (and keep "0.xxx" behavior)
  sanitized = normalizeLeadingZeros(sanitized, props.isAllowedDecimal);

  return sanitized;
};

const removeSeparators = (stringInput: string): string => stringInput.replace(/[,\s]/g, '');

const keepOnlyNumberChars = (stringInput: string): string => stringInput.replace(/[^\d.-]/g, '');

const applyNegativeRule = (stringInput: string, allowNegative: boolean): string => {
  if (!allowNegative) return stringInput.replace(/-/g, '');
  return stringInput.replace(/(?!^)-/g, ''); // remove '-' not at start
};

const applyDecimalRule = (stringInput: string, allowDecimal: boolean, maxDecimals?: number): string => {
  if (!allowDecimal) {
    return stringInput.replace(/\./g, '');
  }

  const singleDotString = keepSingleDot(stringInput);

  if (maxDecimals === undefined) return singleDotString;

  return limitDecimalPlaces(singleDotString, maxDecimals);
};

const keepSingleDot = (stringInput: string): string => {
  const firstDotIndex = stringInput.indexOf('.');
  if (firstDotIndex === -1) return stringInput;

  return stringInput.slice(0, firstDotIndex + 1) + stringInput.slice(firstDotIndex + 1).replace(/\./g, '');
};

const limitDecimalPlaces = (stringInput: string, maxDecimals: number): string => {
  const dotIndex = stringInput.indexOf('.');
  if (dotIndex === -1) return stringInput;

  const [integerPart, decimalPart = ''] = stringInput.split('.');
  return `${integerPart}.${decimalPart.slice(0, Math.max(0, maxDecimals))}`;
};

const isAllowedPartialState = (stringInput: string, allowDecimal: boolean): boolean => {
  if (stringInput === '') return true;
  if (stringInput === '-') return true;
  if (allowDecimal && (stringInput === '.' || stringInput === '-.')) return true;
  return false;
};

const normalizeLeadingZeros = (stringInput: string, allowDecimal: boolean): string => {
  const isNegative = stringInput.startsWith('-');
  const unsigned = isNegative ? stringInput.slice(1) : stringInput;

  if (allowDecimal && unsigned.includes('.')) {
    const [integerPart, decimalPart] = unsigned.split('.');
    const normalizedInt = integerPart.replace(/^0+(?=\d)/, '') || '0';
    return (isNegative ? '-' : '') + normalizedInt + '.' + decimalPart;
  }

  const normalizedInt = unsigned.replace(/^0+(?=\d)/, '');
  return (isNegative ? '-' : '') + normalizedInt;
};

const formatNumberStringWithCommas = (numberString: string): string => {
  if (!numberString) return '';
  if (numberString === '-') return '-';
  if (props.isAllowedDecimal && (numberString === '.' || numberString === '-.')) return numberString;

  const isNegative = numberString.startsWith('-');
  const unsigned = isNegative ? numberString.slice(1) : numberString;

  const [integerPart, decimalPart] = unsigned.split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return (
    (isNegative ? '-' : '') + (decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger)
  );
};

const removeTrailingDecimalZeros = (stringInput: string): string => {
  if (!stringInput.includes('.')) return stringInput;
  return stringInput.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

watch(
  () => modelValue.value,
  (newVal) => {
    rawNumberString.value = newVal === null ? '' : String(newVal);
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.input-error {
  border-color: var(--error);
  box-shadow: none;
}
</style>
